/**
 * The storefront's colour rules the admin needs (CON-9, CON-13, D-ADM-5): a
 * typed, verbatim port of the approved prototype's colour maths
 * (`plans/storefront-v2-design/final/data/proto-helpers.js:17-69`) and of the
 * parts of its `derive()` the store-setup preview draws (`final/theme.js`,
 * Moderno, light mode). The legacy mirror colour is never computed here: the
 * admin reads it only from the API's answers. Hexes are lowercase; the shared
 * vector files are upper case, so comparisons with them ignore case.
 */

/** The six food-friendly hues of never-themed stores without a logo colour (DESIGN.md section 9). */
export type IFoodHueId =
  | 'tomato'
  | 'olive'
  | 'ocean'
  | 'plum'
  | 'mustard'
  | 'charcoal';

export interface IFoodHue {
  id: IFoodHueId;
  /** `#rrggbb`, lowercase. */
  hex: string;
}

export const FOOD_HUES: readonly IFoodHue[] = [
  { id: 'tomato', hex: '#d1462b' },
  { id: 'olive', hex: '#717d2e' },
  { id: 'ocean', hex: '#1f6a8e' },
  { id: 'plum', hex: '#7d3a68' },
  { id: 'mustard', hex: '#d49b1f' },
  { id: 'charcoal', hex: '#3b3734' },
];

type IRgb = [number, number, number];

function hexToRgb(hex: string): IRgb {
  let h = hex.replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** `padStart` is ES2017 and the admin compiles against ES6 (`tsconfig.json` `target`). */
function hexByte(value: number): string {
  const hex = Math.max(0, Math.min(255, Math.round(value))).toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}

function rgbToHex(rgb: readonly number[]): string {
  return '#' + rgb.map(hexByte).join('');
}

function toLin(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function fromLin(c: number): number {
  return (
    255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055)
  );
}

function rgbToOklch([r, g, b]: IRgb): IRgb {
  const R = toLin(r);
  const G = toLin(g);
  const B = toLin(b);
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [L, Math.hypot(a, bb), (Math.atan2(bb, a) * 180) / Math.PI];
}

function oklchToRgbRaw(L: number, C: number, H: number): IRgb {
  const a = C * Math.cos((H * Math.PI) / 180);
  const b = C * Math.sin((H * Math.PI) / 180);
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** Reduces chroma until the colour is in gamut. */
function oklchToHex(L: number, C: number, H: number): string {
  for (let c = C; c >= 0; c -= 0.005) {
    const lin = oklchToRgbRaw(L, c, H);
    if (lin.every((v) => v >= -0.0005 && v <= 1.0005))
      return rgbToHex(lin.map((v) => fromLin(Math.max(0, Math.min(1, v)))));
  }
  return rgbToHex(
    oklchToRgbRaw(L, 0, H).map((v) => fromLin(Math.max(0, Math.min(1, v))))
  );
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLin);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio. */
export function contrast(a: string, b: string): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** The prototype's `ramp()` (proto-helpers.js:49-57), only the two steps the preview uses. */
const RAMP_LIGHTNESS = { 100: 0.95, 700: 0.5 } as const;
const RAMP_CHROMA = { 100: 0.3, 700: 0.92 } as const;
function rampStep(hex: string, step: 100 | 700): string {
  const [, C, H] = rgbToOklch(hexToRgb(hex));
  return oklchToHex(
    RAMP_LIGHTNESS[step],
    Math.max(C, 0.02) * RAMP_CHROMA[step],
    H
  );
}

/** Mixes two colours (t = share of b). */
function mix(a: string, b: string, t: number): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return rgbToHex(x.map((v, i) => v + (y[i] - v) * t));
}

/** Moves `c` toward `toward` until it reaches `target` contrast against every colour of `against`. */
function fit(
  c: string,
  against: readonly string[],
  target: number,
  toward: string
): string {
  const ok = (x: string) => against.every((b) => contrast(x, b) >= target);
  if (ok(c)) return c;
  for (let s = 0.02; s <= 1.0001; s += 0.02) {
    const x = mix(c, toward, s);
    if (ok(x)) return x;
  }
  return toward;
}

const MODERNO_PAPER_LIGHT = '#F5F3EF';
const MODERNO_TINT_LIGHT = 0.34;
const MODERNO_INK_LIGHT = '#191816';
const CREAM = '#FFFAF3';
const NEAR_BLACK = '#16120F';
const WHITE = '#FFFFFF';

/** What the store-setup preview paints: the Moderno style, light mode (DESIGN.md section 3 R1/R2). */
export interface IPreviewColors {
  paper: string;
  surface: string;
  ink: string;
  ink2: string;
  cover: string;
  onCover: string;
  onCover2: string;
  coverOrn: string;
}

export function previewColors(brand: string): IPreviewColors {
  const r100 = rampStep(brand, 100);
  const r700 = rampStep(brand, 700);
  const paper = mix(MODERNO_PAPER_LIGHT, r100, MODERNO_TINT_LIGHT);
  const surface = WHITE;
  const surface2 = mix(paper, WHITE, 0.3);
  const ink = MODERNO_INK_LIGHT;
  const ink2 = fit(mix(ink, paper, 0.44), [paper, surface, surface2], 4.6, ink);
  // Cover field: tone 700 nudged (never the ink) until the ink reads (theme.js:119-127).
  const preferLight = contrast(r700, CREAM) >= 3.2;
  const onCover = preferLight ? CREAM : NEAR_BLACK;
  const cover = fit(r700, [onCover], 4.7, preferLight ? '#000000' : WHITE);
  const onCover2 = fit(mix(onCover, cover, 0.3), [cover], 4.6, onCover);
  const coverOrn = mix(cover, preferLight ? '#000000' : WHITE, 0.13);
  return { paper, surface, ink, ink2, cover, onCover, onCover2, coverOrn };
}

/** White or near-black, whichever reads better on `hex` (proto-helpers.js:59). */
export function swatchInk(hex: string): '#ffffff' | '#111111' {
  return contrast(hex, '#ffffff') >= contrast(hex, '#111111')
    ? '#ffffff'
    : '#111111';
}

/** Same Unicode property escape form as `src/domain/store-setup/store-identity.ts:7` (`/\p{L}/u`). */
function normalizedName(name: string | null | undefined): string {
  return (name ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function fnv1a(value: string): number {
  let h = 0x811c9dc5;
  for (const ch of Array.from(value)) {
    h ^= ch.codePointAt(0) ?? 0;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** The food hue a never-themed store without a logo colour gets (theme.js:38-40, CON-9). */
export function hashedHue(name: string | null | undefined): IFoodHue {
  return FOOD_HUES[fnv1a(normalizedName(name)) % FOOD_HUES.length];
}

/** The colour a store shows before the owner picks one: saved → logo → name (D-TH-2, D-ADM-5). */
export function defaultBrand(input: {
  savedBrand?: string | null;
  logoColor?: string | null;
  name: string;
}): string {
  return (
    input.savedBrand ??
    input.logoColor ??
    hashedHue(input.name).hex
  ).toLowerCase();
}
